import LoadingBackdrop from '@/components/Grid/LoadingBackdrop';
import SearchInput from '@/components/SearchInput';
import { USER_FILTER_LABELS } from '@/constants/userManagement';
import { numberToUsFormat } from '@/helpers/valueFormatter/numberFormatter.helpers';
import { useAppDispatch } from '@/store/hooks';
import { setFilters } from '@/store/slices/userManagementSlice';
import { Button } from '@mui/material';
import { GridToolbarContainer } from '@mui/x-data-grid-pro';
import debounce from 'debounce';
import { ChangeEvent, useCallback, useState } from 'react';
import UserTypeCheckbox from '../../Components/UserTypeCheckbox';
import UserTypeIcon from '../../Components/UserTypeIcon';
import FilterPopover from './FilterPopover';

interface IProps {
  loading: boolean;
  usersTotal: number;
  handleOpenNewUserModal: () => void;
  userRoleFilter: Record<string, unknown>;
  handleUserFilterChange: () => void;
  clients: Record<string, unknown>[];
  filters: Record<string, unknown>;
  selectedUserIds?: number[];
  onClearSelection?: () => void;
  onApplyPermission?: () => void;
}

const ToolBar = ({
  usersTotal,
  handleOpenNewUserModal,
  userRoleFilter,
  handleUserFilterChange,
  loading,
  selectedUserIds = [],
  onClearSelection,
  onApplyPermission,
}: IProps) => {
  const dispatch = useAppDispatch();
  // #region SEARCH
  const [search, setSearch] = useState('');

  const _setFilters = useCallback(
    (newFilters: Record<string, unknown>) => {
      dispatch(setFilters(newFilters));
    },
    [dispatch],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const updateSearchFilter = useCallback(
    debounce(newSearch => _setFilters({ search: newSearch }), 500),
    [_setFilters],
  );

  const onSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const newSearch = event.target?.value;
      setSearch(newSearch);
      updateSearchFilter(newSearch);
    },
    [updateSearchFilter],
  );
  // #endregion

  return (
    <>
      <GridToolbarContainer sx={{ padding: 0 }}>
        <div className='p-[20px] relative w-full'>
          <div className='flex justify-between items-center'>
            <div className='mb-[10px]'>
              <h2 className='mb-[5px]'>
                <span className='text-[#171725] text-[24px] font-semibold'>Total Users</span>
                <span className='text-[#C1BFC2] text-[20px]'>
                  &nbsp;({numberToUsFormat(usersTotal, { maximumFractionDigits: 0 })})
                </span>
              </h2>
            </div>
            <div>
              <Button
                sx={{ height: 40, width: 135 }}
                color='primary'
                variant='contained'
                onClick={handleOpenNewUserModal}
              >
                + Add New
              </Button>
            </div>
          </div>
          {selectedUserIds.length > 0 ? (
            <div className='flex items-center justify-between bg-[#F8F8F8] rounded-[8px] px-[16px] py-[8px] mb-[10px]'>
              <span className='text-[14px] font-semibold'>{selectedUserIds.length} selected</span>
              <div className='flex gap-[10px]'>
                <Button size='small' variant='contained' onClick={onApplyPermission}>
                  Apply Permission
                </Button>
                <Button size='small' variant='outlined' onClick={onClearSelection}>
                  Clear
                </Button>
              </div>
            </div>
          ) : null}
          <div className='flex justify-between'>
            <div className='flex gap-[6px]'>
              {USER_FILTER_LABELS.map(({ name, role, label }, index) => (
                <UserTypeCheckbox
                  key={index}
                  checked={userRoleFilter[name]}
                  disabled={loading}
                  labelIcon={<UserTypeIcon userRole={role} className='w-[15px] mr-[4px]' />}
                  label={label}
                  onChange={handleUserFilterChange}
                  name={name}
                />
              ))}
            </div>
            <div className='table-controls flex gap-[10px]'>
              <SearchInput placeholder='Search..' value={search} onChange={onSearchChange} />
              <FilterPopover setFilters={_setFilters} />
            </div>
          </div>
        </div>
        {loading ? <LoadingBackdrop /> : null}
      </GridToolbarContainer>
    </>
  );
};

export default ToolBar;
